import React, { lazy, Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from "react-router-dom";

import { DashboardPageSkeleton, TemplatesPageSkeleton } from "../components/enhanced-skeletons";
// Temporary Showcase Component
import { ResumeSectionsShowcase } from "../components/marketing/resume-sections-showcase";
import { SimplePageLoader } from "../components/page-loader";
// Keep critical components as regular imports
import { AuthLayout } from "../pages/auth/layout";
import { BuilderLayout } from "../pages/builder/layout";
import { builderLoader } from "../pages/builder/page";
import { DashboardLayout } from "../pages/dashboard/layout";
import { HomeLayout } from "../pages/home/layout";
import { HomePage } from "../pages/home/page";
import { ErrorPage } from "../pages/public/error";
import { publicLoader, publicResumeByIdLoader } from "../pages/public/page";
import { Providers } from "../providers";
import { AppRedirect } from "./app-redirect";
import { AuthGuard } from "./guards/auth";
import { GuestGuard } from "./guards/guest";
import { LocaleGuard } from "./guards/locale";
import { authLoader } from "./loaders/auth";

// Lazy load auth pages
const LoginPage = lazy(() =>
  import("../pages/auth/login/page").then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("../pages/auth/register/page").then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("../pages/auth/forgot-password/page").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("../pages/auth/reset-password/page").then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import("../pages/auth/verify-email/page").then((m) => ({ default: m.VerifyEmailPage })),
);
const VerifyOtpPage = lazy(() =>
  import("../pages/auth/verify-otp/page").then((m) => ({ default: m.VerifyOtpPage })),
);
const BackupOtpPage = lazy(() =>
  import("../pages/auth/backup-otp/page").then((m) => ({ default: m.BackupOtpPage })),
);

// Lazy load dashboard pages
const ResumesPage = lazy(() =>
  import("../pages/dashboard/resumes/page").then((m) => ({ default: m.ResumesPage })),
);
const SettingsPage = lazy(() =>
  import("../pages/dashboard/settings/page").then((m) => ({ default: m.SettingsPage })),
);
const ProfilePage = lazy(() =>
  import("../pages/dashboard/profile/page").then((m) => ({ default: m.ProfilePage })),
);
const DashboardHelpPage = lazy(() =>
  import("../pages/dashboard/help/page").then((m) => ({ default: m.DashboardHelpPage })),
);
const HelpGettingStartedPage = lazy(() =>
  import("../pages/dashboard/help/getting-started").then((m) => ({
    default: m.HelpGettingStartedPage,
  })),
);
const HelpAIFeaturesPage = lazy(() =>
  import("../pages/dashboard/help/ai-features").then((m) => ({ default: m.HelpAIFeaturesPage })),
);
const HelpTemplatesPage = lazy(() =>
  import("../pages/dashboard/help/templates").then((m) => ({ default: m.HelpTemplatesPage })),
);
const HelpAccountPage = lazy(() =>
  import("../pages/dashboard/help/account").then((m) => ({ default: m.HelpAccountPage })),
);
const HelpBillingPage = lazy(() =>
  import("../pages/dashboard/help/billing").then((m) => ({ default: m.HelpBillingPage })),
);
const HelpSummaryGeneratorPage = lazy(() =>
  import("../pages/dashboard/help/summary-generator").then((m) => ({
    default: m.HelpSummaryGeneratorPage,
  })),
);
const TransactionHistoryPage = lazy(() =>
  import("../pages/dashboard/transaction-history/page").then((m) => ({
    default: m.TransactionHistoryPage,
  })),
);
const JobTrackerPage = lazy(() =>
  import("../pages/dashboard/job-tracker/page").then((m) => ({
    default: m.JobTrackerPage,
  })),
);
const JobSearchPage = lazy(() =>
  import("../pages/dashboard/job-search/page").then((m) => ({
    default: m.JobSearchPage,
  })),
);
const JobsIndexPage = lazy(() =>
  import("../pages/jobs/index/page").then((m) => ({ default: m.JobsIndexPage })),
);
const JobsCountryPage = lazy(() =>
  import("../pages/jobs/country/page").then((m) => ({ default: m.JobsCountryPage })),
);
const JobsHubPage = lazy(() =>
  import("../pages/jobs/hub/page").then((m) => ({ default: m.JobsHubPage })),
);
const AIAssistantPage = lazy(() =>
  import("../pages/ai-assistant/page").then((m) => ({ default: m.AIAssistantPage })),
);
const MarketIntelligencePage = lazy(() =>
  import("../pages/market-intelligence/page").then((m) => ({ default: m.MarketIntelligencePage })),
);
const InterviewPage = lazy(() =>
  import("../pages/interview/InterviewPage").then((m) => ({ default: m.InterviewPage })),
);
const VoiceInterviewPage = lazy(() =>
  import("../pages/interview/VoiceInterview").then((m) => ({ default: m.VoiceInterview })),
);

// Lazy load feature pages
const BuilderPage = lazy(() =>
  import("../pages/builder/page").then((m) => ({ default: m.BuilderPage })),
);
const PublicResumePage = lazy(() =>
  import("../pages/public/page").then((m) => ({ default: m.PublicResumePage })),
);
const SmartGeneratorPage = lazy(() =>
  import("../pages/smart-generator/page").then((m) => ({ default: m.SmartGeneratorPage })),
);
const AIDesignerPage = lazy(() =>
  import("../pages/ai-designer/page").then((m) => ({ default: m.AIDesignerPage })),
);
const TemplatesPage = lazy(() =>
  import("../pages/templates/page").then((m) => ({ default: m.TemplatesPage })),
);
const McpDebugPage = lazy(() =>
  import("../pages/mcp-debug").then((m) => ({ default: m.McpDebugPage })),
);
const BadgeGalleryPage = lazy(() =>
  import("../pages/badge-gallery").then((m) => ({ default: m.BadgeGalleryPage })),
);
const StreamingTestPage = lazy(() =>
  import("../pages/streaming-test").then((m) => ({ default: m.default })),
);
const FeaturesPage = lazy(() =>
  import("../pages/features/page").then((m) => ({ default: m.FeaturesPage })),
);
const AgenticGuidePage = lazy(() =>
  import("../pages/features/agentic-guide").then((m) => ({ default: m.AgenticGuidePage })),
);
const SeoDetailsPage = lazy(() =>
  import("../pages/seo/details/page").then((m) => ({ default: m.SeoDetailsPage })),
);
const ManifestoPage = lazy(() =>
  import("../pages/manifesto/page").then((m) => ({ default: m.ManifestoPage })),
);
const AtsCheckerPage = lazy(() =>
  import("../pages/ats-checker/page").then((m) => ({ default: m.AtsCheckerPage })),
);
const StylionPage = lazy(() =>
  import("../pages/stylion/page").then((m) => ({ default: m.StylionPage })),
);
const SummaryRegeneratingPage = lazy(() =>
  import("../pages/summary-regenerating/page").then((m) => ({
    default: m.SummaryRegeneratingPage,
  })),
);

// Lazy load meta pages
const PrivacyPolicyPage = lazy(() =>
  import("../pages/meta/privacy-policy").then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsOfUsePage = lazy(() =>
  import("../pages/meta/terms-of-use").then((m) => ({ default: m.TermsOfUsePage })),
);
const CookiePolicyPage = lazy(() =>
  import("../pages/meta/cookies").then((m) => ({ default: m.CookiePolicyPage })),
);

// Lazy load content pages
const FAQPage = lazy(() => import("../pages/faq/page").then((m) => ({ default: m.FAQPage })));
const ContactPage = lazy(() =>
  import("../pages/contact/page").then((m) => ({ default: m.ContactPage })),
);
const ExamplesPage = lazy(() =>
  import("../pages/examples/page").then((m) => ({ default: m.ExamplesPage })),
);
const AboutPage = lazy(() => import("../pages/about/page").then((m) => ({ default: m.AboutPage })));
const HelpPage = lazy(() => import("../pages/help/page").then((m) => ({ default: m.HelpPage })));

// Lazy load docs pages
const LandingPageIntegrationPage = lazy(() =>
  import("../pages/docs/landing-page-integration").then((m) => ({
    default: m.LandingPageIntegrationPage,
  })),
);

// Lazy load country-specific pages
const SaudiArabiaPage = lazy(() =>
  import("../pages/saudi-arabia/page").then((m) => ({ default: m.SaudiArabiaPage })),
);

// Lazy load embedded pages
const EmbeddedGeneratorPage = lazy(() =>
  import("../pages/embedded-generator/page").then((m) => ({ default: m.EmbeddedGeneratorPage })),
);

// Lazy load payment pages
const PricingPage = lazy(() => import("../pages/dashboard/payment/pricing"));
const PaymentSuccessPage = lazy(() => import("../pages/dashboard/payment/success"));
const PaymentCancelPage = lazy(() => import("../pages/dashboard/payment/cancel"));

export const routes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route errorElement={<ErrorPage />}>
      <Route element={<HomeLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route
          path="features"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <FeaturesPage />
            </Suspense>
          }
        />
        <Route
          path="features/agentic-intelligence"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <AgenticGuidePage />
            </Suspense>
          }
        />
        <Route
          path="details"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <SeoDetailsPage />
            </Suspense>
          }
        />
        <Route
          path="manifesto"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <ManifestoPage />
            </Suspense>
          }
        />
        <Route
          path="templates"
          element={
            <Suspense fallback={<TemplatesPageSkeleton />}>
              <TemplatesPage />
            </Suspense>
          }
        />
        <Route
          path="templates/:category"
          element={
            <Suspense fallback={<TemplatesPageSkeleton />}>
              <TemplatesPage />
            </Suspense>
          }
        />
        {/* Public Job Search hub pages (pSEO) */}
        <Route
          path="jobs"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <JobsIndexPage />
            </Suspense>
          }
        />
        <Route
          path="jobs/:country"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <JobsCountryPage />
            </Suspense>
          }
        />
        <Route
          path="jobs/:country/:role"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <JobsHubPage />
            </Suspense>
          }
        />
        {/* Country-specific pages */}
        <Route
          path="saudi-arabia"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <SaudiArabiaPage />
            </Suspense>
          }
        />
        {/* Meta pages */}
        <Route
          path="meta/privacy-policy"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <PrivacyPolicyPage />
            </Suspense>
          }
        />
        <Route
          path="meta/terms-of-use"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <TermsOfUsePage />
            </Suspense>
          }
        />
        {/* Legacy routes for backward compatibility */}
        <Route
          path="privacy"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <PrivacyPolicyPage />
            </Suspense>
          }
        />
        <Route
          path="terms"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <TermsOfUsePage />
            </Suspense>
          }
        />
        {/* Documentation pages */}
        <Route
          path="docs/landing-page-integration"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <LandingPageIntegrationPage />
            </Suspense>
          }
        />
        {/* Content pages */}
        <Route
          path="faq"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <FAQPage />
            </Suspense>
          }
        />
        <Route
          path="contact"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <ContactPage />
            </Suspense>
          }
        />
        <Route
          path="examples"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <ExamplesPage />
            </Suspense>
          }
        />
        <Route
          path="about"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <AboutPage />
            </Suspense>
          }
        />
        <Route
          path="help"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <HelpPage />
            </Suspense>
          }
        />
        <Route
          path="meta/cookies"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <CookiePolicyPage />
            </Suspense>
          }
        />
        {/* ATS Score Checker - Public */}
        <Route
          path="ats-score-checker"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <AtsCheckerPage />
            </Suspense>
          }
        />
        {/* AI Generator - Public with header/footer */}
        <Route
          path="ai-generator"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <SmartGeneratorPage />
            </Suspense>
          }
        />
        {/* Stylion Resume Roaster - Public */}
        <Route
          path="stylion"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <StylionPage />
            </Suspense>
          }
        />
        <Route
          path="summary-regenerating"
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <SummaryRegeneratingPage />
            </Suspense>
          }
        />
      </Route>

      {/* Public Pricing Route - Accessible to all users */}
      <Route
        path="pricing"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <PricingPage />
          </Suspense>
        }
      />

      <Route path="auth">
        <Route element={<AuthLayout />}>
          <Route element={<GuestGuard />}>
            <Route
              path="login"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <LoginPage />
                </Suspense>
              }
            />
            <Route
              path="register"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <RegisterPage />
                </Suspense>
              }
            />
          </Route>

          {/* Password Recovery */}
          <Route element={<GuestGuard />}>
            <Route
              path="forgot-password"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <ForgotPasswordPage />
                </Suspense>
              }
            />
            <Route
              path="reset-password"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <ResetPasswordPage />
                </Suspense>
              }
            />
          </Route>

          {/* Two-Factor Authentication */}
          <Route element={<GuestGuard />}>
            <Route
              path="verify-otp"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <VerifyOtpPage />
                </Suspense>
              }
            />
            <Route
              path="backup-otp"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <BackupOtpPage />
                </Suspense>
              }
            />
          </Route>

          {/* Email Verification */}
          <Route element={<AuthGuard />}>
            <Route
              path="verify-email"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <VerifyEmailPage />
                </Suspense>
              }
            />
          </Route>

          {/* OAuth Callback */}
          <Route path="callback" loader={authLoader} element={<div />} />
          <Route index element={<Navigate replace to="/auth/login" />} />
        </Route>
      </Route>

      <Route path="dashboard">
        <Route element={<AuthGuard />}>
          <Route element={<DashboardLayout />}>
            <Route
              path="resumes"
              element={
                <Suspense fallback={<DashboardPageSkeleton />}>
                  <ResumesPage />
                </Suspense>
              }
            />
            <Route
              path="job-tracker"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <JobTrackerPage />
                </Suspense>
              }
            />
            <Route
              path="job-search"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <JobSearchPage />
                </Suspense>
              }
            />
            <Route
              path="ai-assistant"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <AIAssistantPage />
                </Suspense>
              }
            />
            <Route
              path="market-intelligence"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <MarketIntelligencePage />
                </Suspense>
              }
            />
            <Route
              path="smart-generator"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <SmartGeneratorPage />
                </Suspense>
              }
            />
            <Route
              path="transaction-history"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <TransactionHistoryPage />
                </Suspense>
              }
            />
            <Route
              path="interview"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <InterviewPage />
                </Suspense>
              }
            />
            <Route
              path="interview/voice"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <VoiceInterviewPage />
                </Suspense>
              }
            />
            <Route
              path="settings"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <SettingsPage />
                </Suspense>
              }
            />
            <Route
              path="profile"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <ProfilePage />
                </Suspense>
              }
            />
            <Route path="help">
              <Route
                index
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <DashboardHelpPage />
                  </Suspense>
                }
              />
              <Route
                path="getting-started"
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <HelpGettingStartedPage />
                  </Suspense>
                }
              />
              <Route
                path="ai-features"
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <HelpAIFeaturesPage />
                  </Suspense>
                }
              />
              <Route
                path="templates"
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <HelpTemplatesPage />
                  </Suspense>
                }
              />
              <Route
                path="account"
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <HelpAccountPage />
                  </Suspense>
                }
              />
              <Route
                path="billing"
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <HelpBillingPage />
                  </Suspense>
                }
              />
              <Route
                path="summary-generator"
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <HelpSummaryGeneratorPage />
                  </Suspense>
                }
              />
            </Route>

            {/* Legacy Payment Routes - Redirect to public pricing */}
            <Route path="payment/pricing" element={<Navigate replace to="/pricing" />} />
            <Route
              path="payment/success"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <PaymentSuccessPage />
                </Suspense>
              }
            />
            <Route
              path="payment/cancel"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <PaymentCancelPage />
                </Suspense>
              }
            />

            <Route index element={<Navigate replace to="resumes" />} />
          </Route>
        </Route>
      </Route>

      <Route
        path="embed-generator"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <EmbeddedGeneratorPage />
          </Suspense>
        }
      />
      <Route path="embedded-generator" element={<Navigate replace to="/embed-generator" />} />
      <Route
        path="ai-designer"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <AIDesignerPage />
          </Suspense>
        }
      />
      <Route
        path="mcp-debug"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <McpDebugPage />
          </Suspense>
        }
      />
      <Route
        path="badge-gallery"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <BadgeGalleryPage />
          </Suspense>
        }
      />
      <Route
        path="streaming-test"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <StreamingTestPage />
          </Suspense>
        }
      />

      {/* Guest-accessible Interview Routes */}
      <Route
        path="interview"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <InterviewPage />
          </Suspense>
        }
      />
      <Route
        path="interview/voice"
        element={
          <Suspense fallback={<SimplePageLoader />}>
            <VoiceInterviewPage />
          </Suspense>
        }
      />

      <Route path="builder">
        <Route element={<AuthGuard />}>
          <Route element={<BuilderLayout />}>
            <Route
              path=":id"
              loader={builderLoader}
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <BuilderPage />
                </Suspense>
              }
            />
            <Route index element={<Navigate replace to="resumes" />} />
          </Route>
        </Route>

        {/* Guest route for trying the builder without authentication */}
        <Route path="try" element={<BuilderLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<SimplePageLoader />}>
                <BuilderPage />
              </Suspense>
            }
          />
        </Route>

        {/* Guest route for specific guest resume by ID */}
        <Route path="guest/:id" element={<BuilderLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<SimplePageLoader />}>
                <BuilderPage />
              </Suspense>
            }
          />
        </Route>

        {/* Public Routes */}
        <Route path="showcase" element={<ResumeSectionsShowcase />} />
      </Route>

      <Route path="resume">
        <Route
          path=":id"
          loader={publicResumeByIdLoader}
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <PublicResumePage />
            </Suspense>
          }
        />
      </Route>

      {/* Reserve /admin and /artboard so they don't match :username/:slug (which would redirect to home).
          If client is wrongly served for these paths, full-page redirect lets nginx serve the correct app. */}
      <Route path="admin" element={<AppRedirect to="/admin/" label="Admin" />} />
      <Route path="admin/*" element={<AppRedirect to="/admin/" label="Admin" />} />
      <Route path="artboard" element={<AppRedirect to="/artboard/" label="Artboard" />} />
      <Route path="artboard/*" element={<AppRedirect to="/artboard/" label="Artboard" />} />

      <Route path=":username">
        <Route
          path=":slug"
          loader={publicLoader}
          element={
            <Suspense fallback={<SimplePageLoader />}>
              <PublicResumePage />
            </Suspense>
          }
        />
      </Route>

      {/* 404 — redirect to home */}
      <Route path="404" element={<Navigate replace to="/" />} />
      <Route path="*" element={<Navigate replace to="/" />} />

      {/* Language-prefixed routes (/:locale/*) */}
      <Route path=":locale">
        <Route element={<LocaleGuard />}>
          <Route element={<HomeLayout />}>
            <Route index element={<HomePage />} />
            <Route
              path="templates"
              element={
                <Suspense fallback={<TemplatesPageSkeleton />}>
                  <TemplatesPage />
                </Suspense>
              }
            />
            <Route
              path="templates/:category"
              element={
                <Suspense fallback={<TemplatesPageSkeleton />}>
                  <TemplatesPage />
                </Suspense>
              }
            />
            {/* Public Job Search hub pages (pSEO) */}
            <Route
              path="jobs"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <JobsIndexPage />
                </Suspense>
              }
            />
            <Route
              path="jobs/:country"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <JobsCountryPage />
                </Suspense>
              }
            />
            <Route
              path="jobs/:country/:role"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <JobsHubPage />
                </Suspense>
              }
            />
            {/* Country-specific pages */}
            <Route
              path="saudi-arabia"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <SaudiArabiaPage />
                </Suspense>
              }
            />
            {/* Meta pages */}
            <Route
              path="meta/privacy-policy"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <PrivacyPolicyPage />
                </Suspense>
              }
            />
            <Route
              path="meta/terms-of-use"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <TermsOfUsePage />
                </Suspense>
              }
            />
            {/* Legacy routes for backward compatibility */}
            <Route
              path="privacy"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <PrivacyPolicyPage />
                </Suspense>
              }
            />
            <Route
              path="terms"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <TermsOfUsePage />
                </Suspense>
              }
            />
            {/* Documentation pages */}
            <Route
              path="docs/landing-page-integration"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <LandingPageIntegrationPage />
                </Suspense>
              }
            />
            {/* Content pages */}
            <Route
              path="faq"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <FAQPage />
                </Suspense>
              }
            />
            <Route
              path="contact"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <ContactPage />
                </Suspense>
              }
            />
            <Route
              path="examples"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <ExamplesPage />
                </Suspense>
              }
            />
            <Route
              path="about"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <AboutPage />
                </Suspense>
              }
            />
            <Route
              path="help"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <HelpPage />
                </Suspense>
              }
            />
            <Route
              path="meta/cookies"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <CookiePolicyPage />
                </Suspense>
              }
            />
            {/* ATS Score Checker - Public */}
            <Route
              path="ats-score-checker"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <AtsCheckerPage />
                </Suspense>
              }
            />
            {/* AI Generator - Public with header/footer */}
            <Route
              path="ai-generator"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <SmartGeneratorPage />
                </Suspense>
              }
            />
            {/* Stylion Resume Roaster - Public */}
            <Route
              path="stylion"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <StylionPage />
                </Suspense>
              }
            />
            <Route
              path="summary-regenerating"
              element={
                <Suspense fallback={<SimplePageLoader />}>
                  <SummaryRegeneratingPage />
                </Suspense>
              }
            />
          </Route>

          {/* Public Pricing Route with locale */}
          <Route
            path="pricing"
            element={
              <Suspense fallback={<SimplePageLoader />}>
                <PricingPage />
              </Suspense>
            }
          />

          <Route path="auth">
            <Route element={<AuthLayout />}>
              <Route element={<GuestGuard />}>
                <Route
                  path="login"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <LoginPage />
                    </Suspense>
                  }
                />
                <Route
                  path="register"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <RegisterPage />
                    </Suspense>
                  }
                />
              </Route>

              {/* Password Recovery */}
              <Route element={<GuestGuard />}>
                <Route
                  path="forgot-password"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <ForgotPasswordPage />
                    </Suspense>
                  }
                />
                <Route
                  path="reset-password"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <ResetPasswordPage />
                    </Suspense>
                  }
                />
              </Route>

              {/* Two-Factor Authentication */}
              <Route element={<GuestGuard />}>
                <Route
                  path="verify-otp"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <VerifyOtpPage />
                    </Suspense>
                  }
                />
                <Route
                  path="backup-otp"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <BackupOtpPage />
                    </Suspense>
                  }
                />
              </Route>

              {/* Email Verification */}
              <Route element={<AuthGuard />}>
                <Route
                  path="verify-email"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <VerifyEmailPage />
                    </Suspense>
                  }
                />
              </Route>

              {/* OAuth Callback */}
              <Route path="callback" loader={authLoader} element={<div />} />
              <Route index element={<Navigate replace to="/auth/login" />} />
            </Route>
          </Route>

          <Route path="dashboard">
            <Route element={<AuthGuard />}>
              <Route element={<DashboardLayout />}>
                <Route
                  path="resumes"
                  element={
                    <Suspense fallback={<DashboardPageSkeleton />}>
                      <ResumesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="job-tracker"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <JobTrackerPage />
                    </Suspense>
                  }
                />
                <Route
                  path="job-search"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <JobSearchPage />
                    </Suspense>
                  }
                />
                <Route
                  path="ai-assistant"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <AIAssistantPage />
                    </Suspense>
                  }
                />
                <Route
                  path="market-intelligence"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <MarketIntelligencePage />
                    </Suspense>
                  }
                />
                <Route
                  path="smart-generator"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <SmartGeneratorPage />
                    </Suspense>
                  }
                />
                <Route
                  path="transaction-history"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <TransactionHistoryPage />
                    </Suspense>
                  }
                />
                <Route
                  path="interview"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <InterviewPage />
                    </Suspense>
                  }
                />
                <Route
                  path="interview/voice"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <VoiceInterviewPage />
                    </Suspense>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <SettingsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <ProfilePage />
                    </Suspense>
                  }
                />
                <Route path="help">
                  <Route
                    index
                    element={
                      <Suspense fallback={<SimplePageLoader />}>
                        <DashboardHelpPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="getting-started"
                    element={
                      <Suspense fallback={<SimplePageLoader />}>
                        <HelpGettingStartedPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="ai-features"
                    element={
                      <Suspense fallback={<SimplePageLoader />}>
                        <HelpAIFeaturesPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="templates"
                    element={
                      <Suspense fallback={<SimplePageLoader />}>
                        <HelpTemplatesPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="account"
                    element={
                      <Suspense fallback={<SimplePageLoader />}>
                        <HelpAccountPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="billing"
                    element={
                      <Suspense fallback={<SimplePageLoader />}>
                        <HelpBillingPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="summary-generator"
                    element={
                      <Suspense fallback={<SimplePageLoader />}>
                        <HelpSummaryGeneratorPage />
                      </Suspense>
                    }
                  />
                </Route>

                {/* Legacy Payment Routes - Redirect to public pricing */}
                <Route path="payment/pricing" element={<Navigate replace to="/pricing" />} />
                <Route
                  path="payment/success"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <PaymentSuccessPage />
                    </Suspense>
                  }
                />
                <Route
                  path="payment/cancel"
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <PaymentCancelPage />
                    </Suspense>
                  }
                />

                <Route index element={<Navigate replace to="resumes" />} />
              </Route>
            </Route>
          </Route>

          <Route
            path="ai-designer"
            element={
              <Suspense fallback={<SimplePageLoader />}>
                <AIDesignerPage />
              </Suspense>
            }
          />

          <Route path="builder">
            <Route element={<AuthGuard />}>
              <Route element={<BuilderLayout />}>
                <Route
                  path=":id"
                  loader={builderLoader}
                  element={
                    <Suspense fallback={<SimplePageLoader />}>
                      <BuilderPage />
                    </Suspense>
                  }
                />
                <Route index element={<Navigate replace to="resumes" />} />
              </Route>
            </Route>

            {/* Guest route for trying the builder without authentication */}
            <Route path="try" element={<BuilderLayout />}>
              <Route
                index
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <BuilderPage />
                  </Suspense>
                }
              />
            </Route>

            {/* Guest route for specific guest resume by ID */}
            <Route path="guest/:id" element={<BuilderLayout />}>
              <Route
                index
                element={
                  <Suspense fallback={<SimplePageLoader />}>
                    <BuilderPage />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>
  </Route>,
);

export const router = createBrowserRouter(routes);
